import { sirenOrSiretLabel } from 'shared/utils/sirenSiret';
import { PersonneMoraleProfileHeader } from 'back-office/shared/components/personnes-morales/PersonneMoraleProfileHeader';
import { profileFromCorporate } from 'back-office/shared/components/personnes-morales/personneMoraleProfile';

import { CorporateDto } from '../api/corporateApi';
import { estAdministree as estModeAdministree } from '../modeGestion';

interface GouvernanceHeaderProps {
  corporate: CorporateDto;
  collaborateursCount?: number | null;
  tunnelsCount?: number | null;
  /** Mode de gestion de la plateforme (GEREE / ADMINISTREE). */
  modeGestion?: string;
}

/**
 * Héro de l'onglet Gouvernance : répond d'un coup d'œil à « qui gère cette
 * plateforme ? ». Le rendu (avatar, chips, stats) est le héro partagé des
 * fiches PM — cette couche n'ajoute que le mode d'administration.
 *
 * La supervision est collective : tous les membres actifs de la PM principale
 * reçoivent automatiquement les briques de gouvernance (mode Gérée). Il n'y a
 * donc plus de détenteur nominatif à désigner ou retirer ici — la PM principale
 * se choisit à la création ou dans l'onglet Portefeuille, et la limitation
 * individuelle passe par les briques par membre.
 */
export const GouvernanceHeader: React.FC<GouvernanceHeaderProps> = ({
  corporate,
  collaborateursCount,
  tunnelsCount,
  modeGestion
}) => {
  const estAdministree = estModeAdministree(modeGestion);

  const description = estAdministree ? (
    <p className="text-sm text-gray-500">
      Le titulaire distribue uniquement les produits autorisés — la gouvernance
      (habilitations, équipe, portefeuille) revient à l'admin.
    </p>
  ) : (
    <p className="text-sm text-gray-500">
      Ses membres actifs gouvernent la plateforme (habilitations, équipe,
      portefeuille) via le Portail Plateforme, sans pouvoir souscrire.
    </p>
  );

  return (
    <PersonneMoraleProfileHeader
      profile={profileFromCorporate(corporate)}
      overline={
        estAdministree
          ? 'Personne morale titulaire'
          : 'Personne morale gérante'
      }
      badges={
        estAdministree ? (
          <span className="flex-shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            Administrée par l'admin
          </span>
        ) : undefined
      }
      description={description}
      stats={[
        { label: 'ORIAS', value: corporate.oriasNumber },
        {
          label: sirenOrSiretLabel(corporate.registrationNumber),
          value: corporate.registrationNumber
        },
        { label: 'Collaborateurs', value: collaborateursCount ?? undefined },
        { label: 'Tunnels habilités', value: tunnelsCount ?? undefined }
      ]}
    />
  );
};
